import { useCallback } from 'react';
import { useApiQueryWithTransport, type Transport } from '@/hooks/use-api-query';
import { useFetch } from '@/hooks/use-fetch';
import { qk } from '@/lib/query/keys';

/** GET /config — backend-relayed runtime config + feature flags. */
export interface AppConfig {
  metaAppId?: string;
  metaConfigId?: string;
  flags?: { instagramEnabled?: boolean; moneyModelV2?: boolean; facebookEnabled?: boolean };
}

/**
 * Whether Instagram connect surfaces are enabled (LaunchDarkly
 * `instagram-enabled`, evaluated server-side, relayed via GET /config).
 *
 * Fail-closed AND fail-soft: while loading or on error the IG tile stays
 * "Coming Soon" (matches the pre-flag production behavior), and the probe
 * opts out of the global ErrorPage escalation — a config blip must not
 * nuke the whole app.
 */
export function useInstagramEnabled(): boolean {
  return useFlags().instagramEnabled ?? false;
}

/**
 * Whether money model v2 (trial + action-metered Build/Scale plans) is live
 * for this deployment (LaunchDarkly `money-model-v2`, relayed via GET
 * /config). Fail-closed: while loading or on error the billing surfaces stay
 * on the legacy message-metered view.
 */
export function useMoneyModelV2(): boolean {
  return useFlags().moneyModelV2 ?? false;
}

/**
 * Whether Facebook Page connect surfaces are enabled (LaunchDarkly
 * `facebook-enabled`, evaluated server-side, relayed via GET /config).
 * Fail-closed: while loading or on error the tile stays hidden.
 */
export function useFacebookEnabled(): boolean {
  return useFlags().facebookEnabled ?? false;
}

function useFlags(): NonNullable<AppConfig['flags']> {
  const fetchWithAuth = useFetch();
  const transport: Transport = useCallback(
    (path, init) => fetchWithAuth(path, init, { suppressGlobalError: true }),
    [fetchWithAuth],
  );
  const { data } = useApiQueryWithTransport<AppConfig>(
    qk.user('app-config'),
    '/config',
    transport,
    { staleTime: 5 * 60_000, retry: false },
  );
  return data?.flags ?? {};
}
